import { AlertTriangle, Shuffle, Wand2, X } from 'lucide-react'
import type { AutoEditParams, EndCardOverlay, TemplateId, TitleCardOverlay } from '@shared/types'
import { useEditorStore } from '../../../store/editorStore'
import { CheckboxField, SelectField, SliderField, TextField } from '../../common/Field'
import { Button } from '../../common/Button'
import { IS_MOCK } from '../../../api'
import { FONT_SELECT_LABEL, FONT_SELECT_ORDER } from './OverlayInspector/fontOptions'

const TEMPLATE_LABEL: Record<TemplateId, string> = {
  opening: '開幕告知型',
  showcase: '作品列挙型',
  breeds: '品種図鑑型',
  hook: '問いかけ/フック型',
  custom: 'カスタム',
}

// REVIEW_v1.md M7 defaults: the target duration/rhythm/includeLogo controls
// used to be `useState(15)` / `useState('standard')` / `useState(true)`
// component-local state that silently reset on every reload — these are
// now the fallback ONLY for a composition that has never run 自動編集 (no
// composition.autoEdit yet); once it has, its own values are read instead.
const DEFAULT_TARGET_DURATION_SEC = 15
const DEFAULT_RHYTHM: AutoEditParams['rhythm'] = 'standard'
const DEFAULT_INCLUDE_LOGO = true
// shared/src/types.ts SectionAssignMode doc comment: "既定 'hue'" — same
// fallback-only-before-first-run pattern as the other 型と尺 controls above.
const DEFAULT_SECTION_ASSIGN: AutoEditParams['sectionAssign'] = 'hue'
// P10-1 (2026-09-06): shared/src/types.ts AutoEditParams.titleFont doc —
// "Undefined = leave the generated overlays' `font` unset (= 'gothic')".
// Same fallback-only-before-first-run pattern as the other 型と尺 controls.
const DEFAULT_TITLE_FONT: AutoEditParams['titleFont'] = 'gothic'

export function TabTemplate() {
  const composition = useEditorStore((s) => s.composition)
  const assets = useEditorStore((s) => s.assets)
  const setTemplate = useEditorStore((s) => s.setTemplate)
  const setSeed = useEditorStore((s) => s.setSeed)
  const updateAutoEditSettings = useEditorStore((s) => s.updateAutoEditSettings)
  const runAutoEdit = useEditorStore((s) => s.runAutoEdit)
  const autoEditRunning = useEditorStore((s) => s.autoEditRunning)
  const autoEditError = useEditorStore((s) => s.autoEditError)
  const autoEditWarnings = useEditorStore((s) => s.autoEditWarnings)
  const dismissAutoEditWarnings = useEditorStore((s) => s.dismissAutoEditWarnings)

  if (!composition) return null

  const targetDurationSec = composition.autoEdit?.targetDurationSec ?? DEFAULT_TARGET_DURATION_SEC
  const rhythm = composition.autoEdit?.rhythm ?? DEFAULT_RHYTHM
  const includeLogo = composition.autoEdit?.includeLogo ?? DEFAULT_INCLUDE_LOGO
  const sectionAssign = composition.autoEdit?.sectionAssign ?? DEFAULT_SECTION_ASSIGN
  const titleFont = composition.autoEdit?.titleFont ?? DEFAULT_TITLE_FONT

  const readyAssets = Object.values(assets).filter((a) => a.ingestStatus === 'ready')
  // The auto-edit engine endpoint is server-owned (S4/S1); until it's live the
  // button stays disabled with a reason instead of silently doing nothing.
  const autoEditUnavailable = !IS_MOCK && readyAssets.length === 0

  async function handleAutoEdit() {
    if (!composition) return
    // Title/EndCard overlays (edited on the Title/終了カード tabs) are the
    // live source of truth for emphasis/seasonBadge/venue/dateText/cta/lines
    // — composition.meta itself has no editor UI of its own, it's only ever
    // *written* (by the engine, from these same AutoEditParams) — reading it
    // back here would silently drop whatever the user just typed on those
    // tabs. Same idea for BGM: the first `bgm` AudioClip on the Audio tab is
    // carried through so a manually-attached BGM survives re-running
    // 自動編集 instead of being wiped when tracks.audio is rebuilt.
    const titleOverlay = composition.tracks.overlays.find((o): o is TitleCardOverlay => o.kind === 'title')
    const endOverlay = composition.tracks.overlays.find((o): o is EndCardOverlay => o.kind === 'end')
    const bgmClip = composition.tracks.audio.find((a) => a.kind === 'bgm')

    await runAutoEdit({
      projectId: composition.projectId,
      assets: Object.values(assets),
      template: composition.template,
      targetDurationSec,
      rhythm,
      seed: composition.seed,
      title: titleOverlay?.text ?? composition.meta.title,
      titleEmphasis: titleOverlay?.emphasis,
      seasonBadge: titleOverlay?.seasonBadge ?? composition.meta.seasonBadge,
      venue: titleOverlay?.venue ?? composition.meta.venue,
      dateText: titleOverlay?.dateText ?? composition.meta.dateText,
      sections: composition.sections.map((s) => ({ name: s.name, nameEn: s.nameEn, color: s.color })),
      endCardLines: endOverlay?.lines ?? (composition.meta.dateText ? [composition.meta.dateText] : []),
      cta: endOverlay?.cta,
      includeLogo,
      sectionAssign,
      titleFont,
      bgm: bgmClip
        ? { assetId: bgmClip.assetId, builtinId: bgmClip.builtinId, gainDb: bgmClip.gainDb }
        : undefined,
    })
  }

  return (
    <div className="p-3 space-y-4">
      <SelectField
        label="型"
        value={composition.template}
        onChange={(e) => setTemplate(e.target.value as TemplateId)}
      >
        {(Object.keys(TEMPLATE_LABEL) as TemplateId[]).map((id) => (
          <option key={id} value={id}>
            {TEMPLATE_LABEL[id]}
          </option>
        ))}
      </SelectField>

      <SliderField
        label="目標尺"
        value={targetDurationSec}
        min={6}
        max={60}
        step={1}
        unit="s"
        onChange={(v) => updateAutoEditSettings({ targetDurationSec: v })}
      />

      <SelectField
        label="リズム"
        value={rhythm}
        onChange={(e) => updateAutoEditSettings({ rhythm: e.target.value as AutoEditParams['rhythm'] })}
      >
        <option value="calm">ゆったり (calm)</option>
        <option value="standard">標準 (standard)</option>
        <option value="brisk">きびきび (brisk)</option>
      </SelectField>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <TextField
            label="シード"
            type="number"
            value={composition.seed}
            onChange={(e) => setSeed(Number(e.target.value))}
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon={<Shuffle size={13} />}
          onClick={() => setSeed(Math.floor(Math.random() * 100000))}
          title="シードをランダム化"
        />
      </div>

      <CheckboxField
        label="ロゴを表示"
        checked={includeLogo}
        onChange={(v) => updateAutoEditSettings({ includeLogo: v })}
      />

      <SelectField
        label="章の割当"
        value={sectionAssign}
        onChange={(e) => updateAutoEditSettings({ sectionAssign: e.target.value as AutoEditParams['sectionAssign'] })}
      >
        <option value="hue">色相で自動（推奨）</option>
        <option value="sequential">時間順（均等割り）</option>
      </SelectField>
      <p className="text-[10px] text-text-faint leading-relaxed -mt-2">
        「色相で自動」は各章の色に合う素材を選んで割り当てます。手で上書きしたSegmentも、次に「自動編集」を押すと上書きされます。
      </p>

      {/* P10-1 (2026-09-06): shared/src/types.ts AutoEditParams.titleFont —
          自動編集が生成するタイトル/終了カードのフォント。既定ゴシック。 */}
      <SelectField
        label="タイトル・終了カードのフォント"
        value={titleFont}
        onChange={(e) => updateAutoEditSettings({ titleFont: e.target.value as AutoEditParams['titleFont'] })}
        data-testid="titlefont-select"
      >
        {FONT_SELECT_ORDER.map((f) => (
          <option key={f} value={f}>
            {FONT_SELECT_LABEL[f]}
          </option>
        ))}
      </SelectField>

      <Button
        variant="primary"
        className="w-full"
        icon={<Wand2 size={14} />}
        onClick={handleAutoEdit}
        disabled={autoEditRunning || autoEditUnavailable}
        data-testid="btn-auto-edit"
      >
        {autoEditRunning ? '自動編集を実行中…' : '自動編集'}
      </Button>
      {autoEditUnavailable && (
        <p className="text-[10px] text-text-faint -mt-2">
          取込済み（ready）の素材がありません。素材を追加してください。
        </p>
      )}
      {autoEditError && <p className="text-[10px] text-accent-red -mt-2">{autoEditError}</p>}
      {autoEditWarnings.length > 0 && (
        <div
          className="rounded border border-amber-500/40 bg-amber-500/10 p-2.5 space-y-1.5"
          data-testid="auto-edit-warnings"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-300">
              <AlertTriangle size={13} />
              自動編集の警告 ({autoEditWarnings.length})
            </div>
            <button
              onClick={dismissAutoEditWarnings}
              className="text-text-faint hover:text-text p-0.5"
              title="閉じる"
            >
              <X size={13} />
            </button>
          </div>
          <ul className="text-[10px] text-amber-200/90 list-disc list-inside space-y-0.5">
            {autoEditWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[10px] text-text-faint leading-relaxed">
        手で調整した後は、再度「自動編集」を押さない限り上書きされません。
      </p>
    </div>
  )
}
